import { Combobox, Decorators, Dictionary, EntityGrid, Fluent, GridUtils, LookupEditor, LookupEditorOptions, QuickFilterBar, SelectEditor, ServiceResponse, ToolButton, Widget, WidgetProps, confirmDialog, faIcon, isEmptyOrNull, isTrimmedEmpty, notifyError, notifyInfo, notifySuccess, notifyWarning, serviceCall, stringFormat, stripDiacritics, trim } from "@serenity-is/corelib";
import { BulkServiceAction, TranslateTextInput, TranslateTextOutput, TranslateTextRequest, TranslateTextResponse, TranslationItem, TranslationListResponse, TranslationTexts, TranslationUpdateRequest, TranslationUpdateResponse } from "@serenity-is/extensions";
import { Column } from "@serenity-is/sleekgrid";
import copyToClipboard from "../Tools/CopyToClipboard";

export interface TranslationGridOptions {
    translateTextEnabled?: boolean;
    parallelRequests?: number;
    batchSize?: number;
}

@Decorators.registerClass('Serenity.Extensions.TranslationGrid')
export class TranslationGrid<P extends TranslationGridOptions = {}> extends EntityGrid<TranslationItem, P> {
    protected getIdProperty() { return "Key"; }
    protected getLocalTextDbPrefix() { return "Site.Translation."; }
    protected getService() { return "Administration/Translation" }

    declare private hasChanges: boolean;
    declare private searchText: string;
    declare private sourceLanguage: LookupEditor;
    declare private targetLanguage: LookupEditor;
    declare private targetLanguageKey: string;

    constructor(props: WidgetProps<P>) {
        super(props);

        this.element.on('keyup.' + this.uniqueName + ' change.' + this.uniqueName,
            'input.custom-text', (e: Event) => {
                const value = Fluent(e.target).val()?.trim() || null;
                this.view.getItemById(Fluent(e.target).data('key')).CustomText = value;
                this.hasChanges = true;
            });
    }

    protected onClick(e: Event, row: number, cell: number): any {
        super.onClick(e, row, cell);

        if (Fluent.isDefaultPrevented(e)) {
            return;
        }

        let item = this.itemAt(row);
        let done: () => void;

        if (Fluent(e.target).hasClass('source-text')) {
            e.preventDefault();

            done = () => {
                item.CustomText = item.SourceText;
                this.view.updateItem(item.Key, item);
                this.hasChanges = true;
            };

            if (isTrimmedEmpty(item.CustomText) ||
                (item.CustomText.trim() === (item.SourceText?.trim() ?? ''))) {
                done();
                return;
            }

            confirmDialog(TranslationTexts.OverrideConfirmation, done);
            return;
        }

        if (Fluent(e.target).hasClass('target-text')) {
            e.preventDefault();

            done = () => {
                item.CustomText = item.TargetText;
                this.view.updateItem(item.Key, item);
                this.hasChanges = true;
            };

            if (isTrimmedEmpty(item.CustomText) ||
                (item.CustomText.trim() === (item.TargetText?.trim() ?? ''))) {
                done();
                return;
            }

            confirmDialog(TranslationTexts.OverrideConfirmation, done);
            return;
        }
    }

    protected getColumns(): Column[] {

        return [{
            field: 'Key',
            width: 400,
            sortable: false
        }, {
            field: 'SourceText',
            width: 400,
            sortable: false,
            format: ctx => <a class="source-text">{ctx.value}</a>,
        }, {
            field: 'CustomText',
            width: 400,
            sortable: false,
            format: ctx => this.options.translateTextEnabled ?
                <div class="input-group w-100">
                    <input class="custom-text form-control py-0" value={ctx.value ?? ""} type="text"
                        placeholder={ctx.item.TargetText} data-key={ctx.item.Key} />
                    <button class="btn btn-primary btn-sm py-0" title={TranslationTexts.TranslateText} onClick={async () => await this.translateText(ctx.item)}>
                        <span class={faIcon("language")} />
                    </button>
                </div>
                : <input class="custom-text w-100" value={ctx.value ?? ""} type="text"
                    placeholder={ctx.item.TargetText} data-key={ctx.item.Key} />
        }];
    }

    protected async translateText(item: TranslationItem) {
        if (!this.options.translateTextEnabled) {
            notifyInfo(TranslationTexts.TranslateTextDisabled);
            return;
        }

        const sourceLanguage = trim(this.sourceLanguage.value || "");
        const targetLanguage = trim(this.targetLanguage.value || "");

        if (isEmptyOrNull(targetLanguage)) {
            notifyError(TranslationTexts.TargetLanguageRequired);
            return;
        }

        if (sourceLanguage == targetLanguage) {
            notifyError(TranslationTexts.SourceTargetLanguageSame);
            return;
        }

        const response = await serviceCall<TranslateTextResponse>({
            service: this.getService() + "/TranslateText",
            request: {
                SourceLanguageID: this.sourceLanguage.value || '',
                Inputs: [{
                    TextKey: item.Key,
                    TargetLanguageID: targetLanguage,
                    SourceText: item.SourceText,
                }]
            } satisfies TranslateTextRequest
        });

        this.updateTranslation(response.Translations[0], item);
    }

    protected updateTranslation(translation: TranslateTextOutput, item: TranslationItem) {
        const inputItem = this.domNode.querySelector(`input.custom-text[data-key="${item[this.getIdProperty()]}"]`) as HTMLInputElement;
        if (inputItem)
            inputItem.value = translation.TranslatedText;

        const viewItem = this.view.getItemById(item[this.getIdProperty()]);
        viewItem.CustomText = translation.TranslatedText;
        viewItem.UserTranslated = true;
        this.view.updateItem(viewItem[this.getIdProperty()], viewItem);
    }

    protected createToolbarExtensions(): void {
        super.createToolbarExtensions();

        let opt: LookupEditorOptions = {
            lookupKey: 'Administration.Language'
        };

        this.sourceLanguage = Widget.create({
            type: LookupEditor,
            element: el => el.appendTo(this.toolbar.domNode).attr('placeholder', '--- ' +
                TranslationTexts.SourceLanguage + ' ---'),
            options: opt
        });

        this.sourceLanguage.changeSelect2(e => {
            if (this.hasChanges) {
                this.saveChanges(this.targetLanguageKey).then(() => this.refresh());
            }
            else {
                this.refresh();
            }
        });

        this.targetLanguage = Widget.create({
            type: LookupEditor,
            element: el => el.appendTo(this.toolbar.domNode).attr('placeholder', '--- ' +
                TranslationTexts.TargetLanguage + ' ---'),
            options: opt
        });

        this.targetLanguage.changeSelect2(e => {
            if (this.hasChanges) {
                this.saveChanges(this.targetLanguageKey).then(() => this.refresh());
            }
            else {
                this.refresh();
            }
        });
    }

    protected getQuickFilters() {
        const filters = super.getQuickFilters();

        const userTranslated = QuickFilterBar.boolean('UserTranslated', TranslationTexts.UserTranslated);
        userTranslated.init = w => w.changeSelect2(() => this.hasChanges && this.saveChanges(this.targetLanguageKey));

        filters.push(userTranslated);

        const hasTranslation = QuickFilterBar.boolean('HasTranslation',
            TranslationTexts.HasTranslation);
        hasTranslation.init = w => w.changeSelect2(() => this.hasChanges && this.saveChanges(this.targetLanguageKey));

        filters.push(hasTranslation);

        filters.push({
            field: 'Assembly',
            cssClass: 'Assembly',
            title: TranslationTexts.Assembly,
            type: SelectEditor
        });

        return filters;
    }

    declare private keysByAssembly: Dictionary<Dictionary<boolean>>;

    protected onViewProcessData(response: TranslationListResponse) {
        response = super.onViewProcessData(response);
        this.keysByAssembly = {};
        const assembly = this.tryFindQuickFilter(SelectEditor, "Assembly");
        if (assembly) {
            let value = assembly.value;
            assembly.clearItems();
            const asmNames = Object.keys(response.KeysByAssembly);
            asmNames.sort();
            for (var asmName of asmNames) {
                var asmKeys = response.KeysByAssembly[asmName];
                if (!asmKeys?.length)
                    continue;
                assembly.addOption(asmName, asmName);
                const keys = this.keysByAssembly[asmName] = {};
                for (var key of asmKeys)
                    keys[key] = true;
            }
            Combobox.getInstance(assembly.domNode)?.setValue(value);
        }
        return response;
    }

    protected saveChanges(language: string): PromiseLike<any> {
        const translations: { [key: string]: string } = {};
        for (let item of this.getItems()) {
            translations[item.Key] = item.CustomText;
        }

        return serviceCall<ServiceResponse>({
            service: this.getService() + "/Update",
            request: {
                TargetLanguageID: language,
                Translations: translations
            } satisfies TranslationUpdateRequest
        }).then((response: TranslationUpdateResponse) => {
            this.hasChanges = false;
            notifySuccess(stringFormat(TranslationTexts.SaveSuccessMessage, response.SavedPath));
        });
    }

    protected onViewSubmit(): boolean {
        const request = this.view.params;
        request.SourceLanguageID = this.sourceLanguage.value;
        this.targetLanguageKey = this.targetLanguage.value || '';
        request.TargetLanguageID = this.targetLanguageKey;
        this.hasChanges = false;
        return super.onViewSubmit();
    }

    protected getButtons(): ToolButton[] {
        const buttons = [
            {
                title: TranslationTexts.SaveChangesButton,
                onClick: (_: any) => this.saveChanges(this.targetLanguageKey).then(() => this.refresh()),
                cssClass: 'apply-changes-button'
            },
            {
                hint: TranslationTexts.CopySourceTranslations,
                cssClass: 'copy-source-to-clipboard',
                icon: faIcon("clone"),
                onClick: () => {
                    var length = this.slickGrid.getDataLength();
                    var d = {};
                    for (var i = 0; i < length; i++) {
                        var item = this.slickGrid.getDataItem(i) as TranslationItem;
                        d[item.Key] = item.SourceText;
                    }

                    copyToClipboard(JSON.stringify(d, null, '  '))
                        .then(() => notifySuccess(TranslationTexts.CopySuccessMessage))
                        .catch(() => notifyError(TranslationTexts.CopyFailMessage))
                }
            },
            {
                hint: TranslationTexts.CopyTargetTranslations,
                cssClass: 'copy-target-to-clipboard',
                icon: faIcon("copy"),
                onClick: () => {
                    var length = this.slickGrid.getDataLength();
                    var d = {};
                    for (var i = 0; i < length; i++) {
                        var item = this.slickGrid.getDataItem(i) as TranslationItem;
                        d[item.Key] = item.CustomText?.length ? item.CustomText :
                            item.HasTranslation ? item.TargetText : null;
                    }

                    copyToClipboard(JSON.stringify(d, null, '  '))
                        .then(() => notifySuccess(TranslationTexts.CopySuccessMessage))
                        .catch(() => notifyError(TranslationTexts.CopyFailMessage))
                }
            }
        ] as ToolButton[];

        if (this.options.translateTextEnabled) {
            buttons.push({
                title: TranslationTexts.TranslateAllText,
                cssClass: 'translate-all-button',
                icon: faIcon("language"),
                onClick: () => {
                    if (!this.options.translateTextEnabled) {
                        notifyInfo(TranslationTexts.TranslateTextDisabled);
                        return;
                    }
            
                    const sourceLanguage = trim(this.sourceLanguage.value || "");
                    const targetLanguage = trim(this.targetLanguage.value || "");
            
                    if (isEmptyOrNull(targetLanguage)) {
                        notifyError(TranslationTexts.TargetLanguageRequired);
                        return;
                    }
            
                    if (sourceLanguage == targetLanguage) {
                        notifyError(TranslationTexts.SourceTargetLanguageSame);
                        return;
                    }    

                    var length = this.slickGrid.getDataLength();
                    var items = [];
                    for (var i = 0; i < length; i++) {
                        var item = this.slickGrid.getDataItem(i) as TranslationItem;
                        if (item.HasTranslation || item.UserTranslated)
                            continue;
                        items.push(item);
                    }

                    if (!items.length) {
                        notifyWarning(TranslationTexts.AllTextsAlreadyTranslated);
                        return;
                    }

                    const bulkAction = new TranslateBulkAction({
                        parallelRequests: this.options.parallelRequests || 1,
                        batchSize: this.options.batchSize || 1,
                        sourceLanguage: sourceLanguage,
                        targetLanguage: targetLanguage,
                        onSuccess: (response, inputs) => {
                            response.Translations.forEach((translation, index) => {
                                let item = this.view.getItemById(translation.TextKey);
                                // sometimes TextKey is returned incorrectly by the service
                                if (!item && inputs?.length == response.Translations.length && inputs[index].TextKey) {
                                    item = this.view.getItemById(inputs[index].TextKey);
                                }
                                this.updateTranslation(translation, item);
                            });
                        }
                    });
                    bulkAction.execute(items);
                }
            });
        }

        return buttons;
    }

    protected createQuickSearchInput() {
        GridUtils.addQuickSearchInputCustom(this.toolbar.domNode,
            (_, searchText, done) => {
                this.searchText = searchText;
                this.view.setItems(this.view.getItems(), true);
                done(this.slickGrid.getDataLength() > 0);
            });
    }

    protected onViewFilter(item: TranslationItem) {
        if (!super.onViewFilter(item))
            return false;

        const userTranslated = this.tryFindQuickFilter(SelectEditor, "UserTranslated");
        if (userTranslated?.value?.length) {
            if (userTranslated.value == "1" && !item.UserTranslated)
                return false;

            if (userTranslated.value == "0" && item.UserTranslated)
                return false;
        }

        const hasTranslation = this.tryFindQuickFilter(SelectEditor, "HasTranslation");
        if (hasTranslation?.value?.length) {
            if (hasTranslation.value == "1" && !item.HasTranslation)
                return false;

            if (hasTranslation.value == "0" && item.HasTranslation)
                return false;
        }

        const assembly = this.tryFindQuickFilter(SelectEditor, "Assembly");
        if (assembly?.value?.length) {
            var kba = (this.keysByAssembly || {})[assembly.value];
            if (!kba || !kba[item.Key])
                return false;
        }

        if (!this.searchText)
            return true;

        const  searching = stripDiacritics(this.searchText).toLowerCase();

        function match(str: string) {
            if (!str)
                return false;

            return str.toLowerCase().indexOf(searching) >= 0;
        }

        return !searching || match(item.Key) || match(item.SourceText) ||
            match(item.TargetText) || match(item.CustomText);
    }

    protected usePager() {
        return false;
    }
}

export interface TranslateBulkActionOptions {
    sourceLanguage: string;
    targetLanguage: string;
    parallelRequests?: number;
    batchSize?: number;
    onSuccess?: (response: TranslateTextResponse, inputs: TranslateTextInput[]) => void;
}

export class TranslateBulkAction extends BulkServiceAction {
    private options: TranslateBulkActionOptions;

    constructor(opt: TranslateBulkActionOptions) {
        super();
        this.options = opt;
    }

    protected getParallelRequests() {
        return this.options.parallelRequests;
    }

    protected getBatchSize() {
        return this.options.batchSize;
    }

    protected override getConfirmationFormat() {
        return TranslationTexts.TranslateTextConfirmation;
    }

    protected executeForBatch(batch: any[]) {
        const inputs = batch.map((item: { Key: any; SourceText: any; }) => ({
            TextKey: item.Key,
            TargetLanguageID: this.options.targetLanguage,
            SourceText: item.SourceText
        } satisfies TranslateTextInput));
        serviceCall<TranslateTextResponse>({
            service: "Administration/Translation/TranslateText",
            request: {
                SourceLanguageID: this.options.sourceLanguage || '',
                Inputs: inputs
            } satisfies TranslateTextRequest,
            blockUI: false,
            onError: () => this.set_errorCount(this.get_errorCount() + batch.length),
            onCleanup: () => this.serviceCallCleanup(),
            onSuccess: (response) => {
                this.set_successCount(this.get_successCount() + batch.length);
                this.options.onSuccess?.(response, inputs);
            },
            errorMode: "none"
        });
    }
}